import styled from '@emotion/styled';
import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from '@updater/ui-uds';
import { useQual, useUpdaterCrossSellUrl } from 'hooks';
import { useTracking } from 'react-tracking';
import { ShopType, SingleProviderConfig } from 'types';
import { OffersContext, MicrositeContext } from 'context';

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 0 auto;
`;

const ServiceMessage = styled(Text)`
  max-width: 500px;
  text-align: center;
`;

const RedirectMessage = styled.div`
  display: block;
  font-size: 16px;
`;

// NOTE: the Cross Selling modal experience should only appear
// for single providers.
export function UpdaterCrossSellModal() {
  const offersContext = useContext(OffersContext);
  const micrositeConfig = useContext(MicrositeContext);
  const { shopType, mainProvider, updaterCrossSellProviders } =
    micrositeConfig as SingleProviderConfig;
  const updaterCrossSellUrl = useUpdaterCrossSellUrl();
  const { primaryData, secondaryData, tertiaryData } = offersContext;
  const decodedQuery = useQual();
  const [countdown, setCountdown] = useState(5);

  // The boolean logic for the updater.com/internet cross-sell is
  // straightforward but requires a lot of checks:
  // (1) no primary offers
  // (2) no secondary offers
  // (3) at least 1 tertiary offer
  // (4) not already on the Updater cross-sell — the redirect below sets
  //     qual.microsite = 'Updater', so reopening the modal on the destination
  //     bounces it back to itself in an infinite loop (the destination keeps
  //     resolving to a single-provider config). Cross-sell at most once.
  const modalIsOpen =
    shopType === ShopType.SINGLE_PROVIDER &&
    decodedQuery?.microsite !== 'Updater' &&
    updaterCrossSellProviders &&
    updaterCrossSellProviders?.length > 0 &&
    primaryData.loading === false &&
    primaryData.availableOffers?.length === 0 &&
    secondaryData.loading === false &&
    secondaryData.availableOffers?.length === 0 &&
    tertiaryData.loading === false &&
    tertiaryData.availableOffers?.length > 0;

  function closeModal() {}

  const { trackEvent } = useTracking({
    object: 'updater_cross_sell_modal',
    details: {
      // provider: topCrossSellProvider ? topCrossSellProvider.name : '',
    },
  });

  useEffect(() => {
    if (modalIsOpen) {
      trackEvent({
        verb: 'viewed',
      });
    }
  }, [modalIsOpen, trackEvent]);

  // The only difference between the two quals should be the
  // provider (microsite) key to load Updater.com/internet and
  // the GTM code.
  // The promoID should be the promoID of the origin site, not
  // the UCI promoID.
  // Use the `microsite` keyword and delete the `provider` key.
  // Sites that haven't moved from the old Shop App config may
  // contain two and the microsite key will take precedence.
  const mutatedQual = useMemo(() => {
    const qual = { ...decodedQuery };
    qual.microsite = 'Updater';
    qual.gtmCode = 'GTM-M356222';
    delete qual.provider;
    return qual;
  }, [decodedQuery]);

  const getRedirectUrl = useCallback(() => {
    const encodedQual = btoa(JSON.stringify(mutatedQual));
    return `${updaterCrossSellUrl}/qualify?qual=${encodedQual}&uuid=${mutatedQual.uuid}&skipToPlans=true`;
  }, [mutatedQual, updaterCrossSellUrl]);

  // redirect after countdown
  useEffect(() => {
    if (!modalIsOpen) return;

    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      window.location.assign(getRedirectUrl());
    }
  }, [
    modalIsOpen,
    countdown,
    mutatedQual,
    updaterCrossSellUrl,
    getRedirectUrl,
  ]);

  const handleRedirect = useCallback(() => {
    trackEvent({ verb: 'skipped' });
    window.location.assign(getRedirectUrl());
  }, [getRedirectUrl, trackEvent]);

  const displayName = mainProvider ? mainProvider.displayName : '';

  return (
    <Modal isOpen={modalIsOpen} setIsOpen={closeModal}>
      <ModalOverlay cursor="default" />
      <ModalContent maxWidth="736" minWidth="1" minHeight="1">
        <ModalBody padding="l" marginBottom="xxxxl">
          <Container>
            <Text
              as="h1"
              variant={['mBold', null, 'xlBold', null, null]}
              marginBottom="l"
              textAlign="center"
            >
              {displayName} is not available at your address but the following
              provider(s) are!
            </Text>
            <Flex flexWrap="wrap" justifyContent="center" marginBottom="l">
              {tertiaryData.availableProviders?.map((s12yProvider, idx) => (
                <Flex
                  key={`uci_logo_${idx}`}
                  width="150px"
                  height="50px"
                  alignItems="center"
                  margin="s"
                >
                  <Image
                    src={s12yProvider.logo.url}
                    alt={s12yProvider.name}
                    width="100%"
                    height="auto"
                  />
                </Flex>
              ))}
            </Flex>
            <ServiceMessage variant="m" marginBottom="l">
              Enter Updater, the easiest place to shop and compare internet
              plans all in one place.
            </ServiceMessage>
            <RedirectMessage>
              Taking you to Updater shopping in {countdown}...
            </RedirectMessage>
            <Button
              variant="primary"
              onClick={handleRedirect}
              marginTop="l"
              size="l"
              data-testid="skip-button"
              dataCy="skip-button"
            >
              Continue now
            </Button>
          </Container>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
